import React, { useState } from 'react';
import { Heart, X, Check, Copy, Wallet, ExternalLink, Smartphone, Monitor, Globe, Shield } from 'lucide-react';

const walletOptions = [
  {
    id: 'gui',
    name: 'Monero GUI',
    icon: Monitor,
    description: 'Official desktop wallet',
    downloadUrl: 'https://www.getmonero.org/downloads/',
    protocol: 'monero:',
    platform: 'desktop',
    color: '#FF6600'
  },
  {
    id: 'monerujo',
    name: 'Monerujo',
    icon: Smartphone,
    description: 'Android wallet',
    downloadUrl: 'https://www.monerujo.io/',
    protocol: 'monero:',
    platform: 'android',
    color: '#4CAF50'
  },
  {
    id: 'cakewallet',
    name: 'Cake Wallet',
    icon: Smartphone,
    description: 'iOS & Android wallet',
    downloadUrl: 'https://cakewallet.com/',
    protocol: 'monero:',
    platform: 'mobile',
    color: '#9C27B0'
  },
  {
    id: 'feather',
    name: 'Feather Wallet',
    icon: Monitor,
    description: 'Lightweight desktop wallet',
    downloadUrl: 'https://featherwallet.org/',
    protocol: 'monero:',
    platform: 'desktop',
    color: '#2196F3'
  },
  {
    id: 'mymonero',
    name: 'MyMonero',
    icon: Globe,
    description: 'Web & mobile wallet',
    downloadUrl: 'https://mymonero.com/',
    protocol: 'monero:',
    platform: 'web',
    color: '#FF9800'
  }
];

export const Donations: React.FC = () => {
  const [copiedDonation, setCopiedDonation] = useState(false);
  const [donationAmount, setDonationAmount] = useState('');
  const donationAddress = "875hv6ZqLtSUbkGpZNXaQU745BKxgJdXd8zvTsFPhv5mE6eUiiP97bcS16xjfT55xmEc746CPLCjWDcEFE5VXAeVVgaJbtS";
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=monero:${donationAddress}&format=png&bgcolor=FFFFFF&color=000000&margin=10`;

  const handleCopyDonation = async () => {
    try {
      await navigator.clipboard.writeText(donationAddress);
      setCopiedDonation(true);
      setTimeout(() => setCopiedDonation(false), 2000);
    } catch (err) {
      console.error('Failed to copy donation address:', err);
    }
  };

  const handleWalletSend = (walletId: string) => {
    const wallet = walletOptions.find(w => w.id === walletId);
    if (!wallet) return;

    let moneroUri = `${wallet.protocol}${donationAddress}`;
    if (donationAmount && parseFloat(donationAmount) > 0) {
      moneroUri += `?tx_amount=${donationAmount}`;
    }

    try {
      if (wallet.platform === 'mobile' || wallet.platform === 'android') {
        window.location.href = moneroUri;
      } else {
        navigator.clipboard.writeText(moneroUri).then(() => {
          alert(`Monero URI copied to clipboard!\n\nPaste this into ${wallet.name}:\n${moneroUri}`);
        }).catch(() => {
          prompt(`Copy this Monero URI and paste it into ${wallet.name}:`, moneroUri);
        });
      }
    } catch (error) {
      console.error('Failed to open wallet:', error);
      navigator.clipboard.writeText(moneroUri).catch(() => {
        prompt(`Copy this Monero URI:`, moneroUri);
      });
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="bg-gray-900 rounded-2xl border border-gray-700 w-full max-w-2xl mx-auto shadow-2xl">
        <div className="p-4 sm:p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-white">Support XMRmap</h2>
                <p className="text-gray-400 text-sm">Help us maintain this privacy-focused service</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          <div className="text-center">
            <div className="w-32 h-32 sm:w-48 sm:h-48 bg-white rounded-xl mx-auto mb-4 flex items-center justify-center overflow-hidden">
              <img 
                src={qrCodeUrl} 
                alt="XMR Donation QR Code" 
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement!.innerHTML = '<div class="flex flex-col items-center justify-center w-full h-full text-gray-800"><div class="w-12 h-12 mb-2 flex items-center justify-center"><svg class="w-full h-full" fill="currentColor" viewBox="0 0 24 24"><path d="M3 11h8V3H3v8zm2-6h4v4H5V5zM3 21h8v-8H3v8zm2-6h4v4H5v-4zM13 3v8h8V3h-8zm6 6h-4V5h4v4zM19 13h2v2h-2zM13 13h2v2h-2zM15 15h2v2h-2zM13 17h2v2h-2zM15 19h2v2h-2zM17 17h2v2h-2zM17 13h2v2h-2zM19 15h2v2h-2z"/></svg></div><span class="text-sm">QR Code</span></div>';
                }}
              />
            </div>
            <p className="text-gray-300 text-sm mb-4">
              Scan QR code with your Monero wallet or use the options below
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Donation Amount (Optional)
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.001"
                min="0"
                value={donationAmount}
                onChange={(e) => setDonationAmount(e.target.value)}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="0.1"
              />
              <span className="absolute right-3 top-3 text-gray-400 text-sm">XMR</span>
            </div>
          </div>

          <div>
            <h3 className="text-base sm:text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
              Send with Your Wallet
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {walletOptions.map((wallet) => (
                <button
                  key={wallet.id}
                  onClick={() => handleWalletSend(wallet.id)}
                  className="flex items-center gap-3 p-3 sm:p-4 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-gray-600 rounded-lg transition-all group"
                >
                  <div 
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: wallet.color }}
                  >
                    <wallet.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="text-white font-medium group-hover:text-orange-300 transition-colors text-sm sm:text-base">
                      {wallet.name}
                    </h4>
                    <p className="text-gray-400 text-xs">{wallet.description}</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-orange-400 transition-colors" />
                </button>
              ))}
            </div>
            <p className="text-gray-500 text-xs mt-3">
              Don't have a wallet? Click any option above to download and get started.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Or Copy Address Manually
            </label>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <input
                type="text"
                value={donationAddress}
                readOnly
                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs font-mono break-all"
              />
              <button
                onClick={handleCopyDonation}
                className="px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors flex items-center justify-center gap-1 flex-shrink-0"
              >
                {copiedDonation ? (
                  <>
                    <Check className="w-4 h-4" />
                    <span className="text-sm">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span className="text-sm">Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="text-orange-300 font-medium mb-1">Privacy-First Donations</p>
                <p className="text-orange-200/80 leading-relaxed">
                  Your donations help us maintain XMRmap and support businesses that embrace financial privacy. 
                  All donations are processed anonymously through Monero's private blockchain.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-400 text-sm">
              Thank you for supporting privacy-focused commerce! 🧡
            </p>
            <p className="text-gray-500 text-xs mt-1">
              Every donation helps keep XMRmap free and open-source
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
